/**
 * Which call-to-action the Buy / Book gate shows on a listing.
 *
 * account-first (§5 gate): an account is the first barrier. Since the e-mail-only sign-in
 * (api#402, step 2) it is also the only session: a person signs in by e-mail and code, and the
 * wallet that pays is one linked to that account (Profile → Linked wallets), connected at the
 * moment of paying. Every buyer command is `@Protected({ forWallet, forAccount })` and resolves
 * the account session to its primary wallet — an account with no wallet linked is refused with
 * 403 WALLET_NOT_LINKED, so the gate sends it to link one before it reaches a dead purchase.
 *
 * Which *family* the wallet has to be of is the next question, not this one: the escrow offer and
 * the Buy Now dialog read the chain registry and the account's wallets and say so in their own
 * words (`resolveEscrowOffer`, `resolvePurchaseChainOptions`).
 *
 * Free browsing stays open: this only governs the action button, not viewing the listing.
 */
export type PurchaseGate = 'buy' | 'open-in-browser' | 'link-wallet' | 'sign-up';

export interface PurchaseGateSession {
  /** An account session is held (`/user/auth/me`) — the identity that carries a role. */
  isLoggedIn: boolean;
  /**
   * The account holds a linked wallet (`primaryWalletId` on `/user/auth/me` — the first wallet an
   * account links becomes its primary one for good). `undefined` means "not known yet": the
   * account is still being read.
   */
  hasLinkedWallet?: boolean;
  /** Running inside the Telegram mini-app, where the wallet adapter cannot open. */
  isTMA: boolean;
}

export const resolvePurchaseGate = ({ isLoggedIn, hasLinkedWallet, isTMA }: PurchaseGateSession): PurchaseGate => {
  // No account: register first — registration works inside Telegram too.
  if (!isLoggedIn) return 'sign-up';

  // Inside Telegram's built-in browser no wallet adapter can open: neither linking a wallet nor
  // paying or signing with one is possible there, so for every signed-in account the way on is a
  // real browser. Before the e-mail-only sign-in an account could not even be signed in with a
  // wallet there; now it can hold a linked one, and a live Buy or Book that ends in "no wallet
  // found" — or, for a rental, in a booking that cannot be paid — would be worse than the bounce.
  if (isTMA) return 'open-in-browser';

  // An account with no wallet at all has nothing to pay with: it links one in Profile.
  if (hasLinkedWallet === false) return 'link-wallet';

  // Linked — or not known yet, which keeps the pre-existing behaviour rather than flashing a wrong
  // CTA; if the optimistic guess is wrong the server refuses the action and the screen says why.
  return 'buy';
};
